import type { FileRecordInfo } from "../../Album/types/attachment.types.ts"

interface ProjectDocumentLead {
  id: string
}

/** The business records a document may optionally link to. */
const RELATED_RECORD_TYPES = [
  "project",
  "lead",
  "proposal",
  "contract",
  "invoice"
] as const

type RelatedRecordType = (typeof RELATED_RECORD_TYPES)[number]

interface RelatedRecord {
  type: RelatedRecordType
  id: string
  label: string
}

interface ProjectDocument {
  id: string
  lead: ProjectDocumentLead
  file_name: string
  file: string
  // A label string on list, the stored integer everywhere else.
  file_type: number | string | null
  file_type_name: string | null
  uploaded_by: string | null
  // The date on the paper, a bare `YYYY-MM-DD` day. `created` is when the row appeared.
  document_date?: string
  created: string
  updated: string
  version: number
  is_current?: boolean
  revision_note?: string | null
  file_size?: number | null
  formatted_file_size?: string | null
  folder?: string | null
  // Business classification — independent of `file_type` above, both optional.
  document_type?: string | null
  document_type_label?: string | null
  // The workflow status of the logical document — belongs to whichever
  // version is current, `null` for the (large majority of) files that never
  // opt into a workflow. Changed only via `changeDocumentStatus`.
  status?: string | null
  status_label?: string | null
  // Which statuses `change-status` will actually accept from here — the
  // same transition graph the endpoint enforces, so the picker can offer
  // only legal moves. Empty for a terminal status (nothing further to pick).
  next_statuses?: string[]
  related_record?: RelatedRecord | null
  deleted_at?: string | null
  // A plain display name (`StringRelatedField` server-side), not an id.
  deleted_by?: string | null
  // Customer Portal visibility — written only through
  // `shareDocumentWithCustomer`/`unshareDocumentFromCustomer`, never a bare
  // PATCH (mirrors the backend's own read_only_fields).
  is_visible_to_customer?: boolean
  shared_with_customer_at?: string | null
  // Explicit "stay internal even though the folder I'm in is customer-
  // visible" override — a plain writable field, unlike the pair above.
  customer_visibility_excluded?: boolean
  // The folder chain a customer may be shown for this document — see the
  // backend's `visible_breadcrumb` for exactly what "truncated at the
  // first invisible ancestor" means. Always `[]` outside the customer
  // portal's own read of this same serializer.
  folder_path?: { id: string; name: string }[]
  // Origin, not a document type or review status — read server-side off
  // who actually uploaded the row.
  customer_uploaded?: boolean
  // Additive, alongside `lead` above (kept `{id, name}` for every existing
  // per-project caller) — Global Files needs the uid/customer too.
  project_info?: FileRecordInfo | null
  // Present only when Global Files' unscoped fetch asked for it — `true`
  // means this document genuinely has no workspace (Company Files, a
  // first-class state), distinct from a per-project caller where the key
  // is absent because the question was never asked.
  is_company_scoped?: boolean
}

interface ProjectDocumentsParams {
  // Omitted only by Global Files, which asks across every accessible
  // project at once — every other caller still scopes to one.
  lead?: string
  page?: number
  page_size?: number
  // DRF's SEARCH_PARAM is renamed to `q`; `search` is silently ignored.
  q?: string
  folder?: string
  folder__isnull?: boolean
  document_type?: string
  related_record_type?: RelatedRecordType
  related_record_id?: string
  trashed?: boolean
  ordering?: string
  // A document's workflow status — an exact-match filter field the backend
  // already supports (`ProjectAttachmentFilter.Meta.fields`), just never
  // sent from here before Global Files' filter drawer.
  status?: string
  // Global Files' Location filter (EAD-615 Phase B) — read directly off the
  // query params server-side (`ProjectAttachmentViewSet.get_queryset`), not
  // a `django_filters` field, and so never present on `Meta.fields`. Never
  // sent by a per-project caller, which always already knows its own single
  // workspace via `lead`.
  location?: "company" | "project" | "lead"
  // Comma-joined `crm.Customer` ids — "uploaded by this specific customer",
  // mirroring the per-project Files drawer's own Origin/Customer control.
  customer?: string
  // Global Files' Document Health drill-through (EAD-615 Phase B follow-up)
  // — read directly off the query params server-side
  // (`ProjectAttachmentViewSet.get_queryset`), same convention as `location`.
  attention?: string
  // Global Trash's "Deleted Date" filter — exact UTC bounds, mirroring
  // `created`'s own from/to range shape.
  deleted_after?: string
  deleted_before?: string
}

interface ProjectDocumentBulkFailure {
  id: string
  type: string
  message: string
}

interface ProjectDocumentBulkResult {
  requested: number
  succeeded: string[]
  failed: ProjectDocumentBulkFailure[]
}

interface ProjectDocumentMoveData {
  folder: string | null
}

/** `null` on either pair clears the link; omitting a field leaves it alone. */
interface ProjectDocumentClassificationData {
  document_type?: string | null
  related_record_type?: RelatedRecordType | null
  related_record_id?: string | null
}

interface ProjectDocumentTypeOption {
  label: string
  value: number
  // Draft is filter-only: the bucket untyped uploads land in, never a choice.
  visible_in: Array<"filter" | "form">
}

interface ProjectDocumentFilterOptions {
  file_type: ProjectDocumentTypeOption[]
}

interface ProjectDocumentUploadData {
  /** Omit for a Company/General upload (EAD-615 Phase B) — the server
   * derives `team` from the caller when no workspace is given. */
  lead?: string
  file_name: string
  file: File
  file_type?: number
  folder?: string
  document_type?: string
  // Set to upload a new version of an existing document rather than a
  // brand-new one; file-level metadata omitted here is carried forward from
  // the version this points at.
  previous_version_id?: string
  revision_note?: string
}

export { RELATED_RECORD_TYPES }
export type {
  ProjectDocument,
  ProjectDocumentBulkFailure,
  ProjectDocumentBulkResult,
  ProjectDocumentClassificationData,
  ProjectDocumentFilterOptions,
  ProjectDocumentLead,
  ProjectDocumentMoveData,
  ProjectDocumentTypeOption,
  ProjectDocumentUploadData,
  ProjectDocumentsParams,
  RelatedRecord,
  RelatedRecordType
}
